import {
    type AssignedApp,
    type NavigationEntry,
    type NavigationIcon,
    resolveLocalizedName,
} from '@luminovo/apps-types';

import type { AddressableSlotAssignment } from '../api/fetchSlotAssignments';
import type { FullPageSlotAssignment } from './matchFullPageAssignment';

/** How the rail draws an entry's icon: a lucide icon by name, or an image served from the app's bundle. */
export type FullPageNavigationIcon = { kind: 'name'; name: string } | { kind: 'image'; url: string; alt: string };

export interface FullPageNavigationItem {
    appId: string;
    /** The concrete product path the entry links to: a pathname, optionally with query string and fragment. */
    path: string;
    label: string;
    icon: FullPageNavigationIcon | undefined;
    /** Nested entries, in the order they are shown. Empty for a leaf. */
    children: FullPageNavigationItem[];
}

export interface FullPageAppNavigation {
    app: AssignedApp;
    /** The rail's entry for the app itself; its children are the app's pages, nested as declared. */
    root: FullPageNavigationItem;
    /** Whether the app declared its Navigation or the tree was derived from its routes. */
    source: 'declared' | 'derived';
    /** All full-page routes for matching an active app, including routes no entry names. */
    assignments: FullPageSlotAssignment[];
}

const routeSegments = (route: string): string[] => route.split('/').filter(Boolean);
const isStaticRoute = (route: string): boolean =>
    !routeSegments(route).some((segment) => segment.startsWith(':') || segment === '*');

const isSegmentAncestor = (ancestor: string, descendant: string): boolean => {
    const ancestorSegments = routeSegments(ancestor);
    const descendantSegments = routeSegments(descendant);
    return (
        ancestorSegments.length <= descendantSegments.length &&
        ancestorSegments.every((segment, index) => segment === descendantSegments[index])
    );
};

const isProperSegmentAncestor = (ancestor: string, descendant: string): boolean =>
    routeSegments(ancestor).length < routeSegments(descendant).length && isSegmentAncestor(ancestor, descendant);

const stableCompare = (left: string, right: string): number => (left === right ? 0 : left < right ? -1 : 1);

const compareItems = (left: FullPageNavigationItem, right: FullPageNavigationItem): number =>
    stableCompare(left.label.normalize(), right.label.normalize()) ||
    stableCompare(left.path, right.path) ||
    stableCompare(left.appId, right.appId);

const groupFullPageAssignmentsByApp = (
    assignments: readonly AddressableSlotAssignment[],
): FullPageSlotAssignment[][] => {
    const byApp = new Map<string, FullPageSlotAssignment[]>();
    for (const assignment of assignments) {
        if (assignment.widget.kind !== 'full-page') continue;
        const appAssignments = byApp.get(assignment.app.id) ?? [];
        appAssignments.push(assignment as FullPageSlotAssignment);
        byApp.set(assignment.app.id, appAssignments);
    }
    return [...byApp.values()];
};

const derivedItem = (assignment: FullPageSlotAssignment, locale: string): FullPageNavigationItem => ({
    appId: assignment.app.id,
    path: assignment.widget.route,
    label: resolveLocalizedName(assignment.widget.name, locale),
    icon: undefined,
    children: [],
});

/**
 * The derived convention, for an app that declares no Navigation: the unique declared static
 * route that is a segment ancestor of every full-page route is the app's entry, every other
 * static route below it is a child sorted by label, and dynamic or wildcard routes are hidden.
 * Undefined when the routes identify no single root — the app is then omitted from the rail.
 */
const deriveFromRoutes = (
    appAssignments: readonly FullPageSlotAssignment[],
    locale: string,
): FullPageNavigationItem | undefined => {
    const mainCandidates = appAssignments.filter(
        (candidate) =>
            isStaticRoute(candidate.widget.route) &&
            appAssignments.every((assignment) => isSegmentAncestor(candidate.widget.route, assignment.widget.route)),
    );
    if (mainCandidates.length !== 1) return undefined;

    const mainAssignment = mainCandidates[0];
    const children = appAssignments
        .filter(
            (assignment) =>
                assignment !== mainAssignment &&
                isStaticRoute(assignment.widget.route) &&
                isSegmentAncestor(mainAssignment.widget.route, assignment.widget.route),
        )
        .map((assignment) => derivedItem(assignment, locale))
        .sort(compareItems);

    return { ...derivedItem(mainAssignment, locale), children };
};

// An asset icon needs the app's assets base to become a URL. Without one this host has no
// addressable bundle files, so the entry keeps its label and simply has no icon.
const declaredIcon = (
    icon: NavigationIcon | undefined,
    assetsBaseUrl: string | undefined,
): FullPageNavigationIcon | undefined => {
    if (icon === undefined) return undefined;
    if (typeof icon === 'string') return { kind: 'name', name: icon };
    if (assetsBaseUrl === undefined) return undefined;
    return { kind: 'image', url: `${assetsBaseUrl}${icon.path}`, alt: icon.alt };
};

const declaredItem = (
    appId: string,
    entry: NavigationEntry,
    locale: string,
    assetsBaseUrl: string | undefined,
): FullPageNavigationItem => ({
    appId,
    path: entry.path,
    label: resolveLocalizedName(entry.label, locale),
    icon: declaredIcon(entry.icon, assetsBaseUrl),
    children: entry.children.map((child) => declaredItem(appId, child, locale, assetsBaseUrl)),
});

/**
 * Builds each app's rail tree. An app that declares `navigation` gets its declared tree verbatim
 * — the platform already checked every path against the app's routes at upload, and declaration
 * order is the display order. Every other app falls back to the route-derived convention.
 * Assignment response order never participates.
 */
export function deriveFullPageAppNavigation(
    assignments: readonly AddressableSlotAssignment[],
    locale: string,
): FullPageAppNavigation[] {
    const navigation: FullPageAppNavigation[] = [];
    for (const appAssignments of groupFullPageAssignmentsByApp(assignments)) {
        const [first] = appAssignments;
        if (first === undefined) continue;

        const declared = first.app.navigation;
        if (declared !== undefined) {
            // Per-app on the wire, so any assignment carries the same base.
            const assetsBaseUrl = appAssignments.find(
                (assignment) => assignment.assets_base_url !== undefined,
            )?.assets_base_url;
            navigation.push({
                app: first.app,
                root: declaredItem(first.app.id, declared, locale, assetsBaseUrl),
                source: 'declared',
                assignments: [...appAssignments],
            });
            continue;
        }

        const root = deriveFromRoutes(appAssignments, locale);
        if (root === undefined) continue;
        navigation.push({ app: first.app, root, source: 'derived', assignments: [...appAssignments] });
    }

    return navigation.sort((left, right) => compareItems(left.root, right.root));
}

/** An app and the top-level destinations it contributes to a flat listing. */
export interface AppTopLevelRoutes {
    app: AssignedApp;
    source: 'declared' | 'derived';
    routes: { path: string; label: string }[];
}

/**
 * Every top-level destination an app contributes. A declared Navigation has one, its root; an
 * app whose rail is derived from routes has one per static route with no other static route of
 * the same app above it. Apps that contribute none are left out.
 */
export function deriveTopLevelAppRoutes(
    assignments: readonly AddressableSlotAssignment[],
    locale: string,
): AppTopLevelRoutes[] {
    return groupFullPageAssignmentsByApp(assignments)
        .map((appAssignments): AppTopLevelRoutes => {
            const { app } = appAssignments[0];
            const declared = app.navigation;
            if (declared !== undefined) {
                const label = resolveLocalizedName(declared.label, locale);
                return { app, source: 'declared', routes: [{ path: declared.path, label }] };
            }

            const staticRoutes = appAssignments.filter((assignment) => isStaticRoute(assignment.widget.route));
            const byPath = new Map<string, { path: string; label: string }>();
            for (const { widget } of staticRoutes) {
                if (byPath.has(widget.route)) continue;
                if (staticRoutes.some((other) => isProperSegmentAncestor(other.widget.route, widget.route))) continue;
                byPath.set(widget.route, {
                    path: widget.route,
                    label: resolveLocalizedName(widget.name, locale),
                });
            }
            return { app, source: 'derived', routes: [...byPath.values()] };
        })
        .filter((entry) => entry.routes.length > 0);
}
